import React, { useMemo } from 'react';
import { useState } from 'react';
import { X, Loader2, Sparkles, BookOpen, Quote, ArrowRight, CheckCircle, History, Lightbulb, AlignLeft, ListChecks, Wand2 } from 'lucide-react';
import { enhanceText } from '../lib/openai';

interface TextEnhancerProps {
  selectedText: string;
  onClose: () => void;
  onApply: (enhancedText: string) => void;
}

type EnhancementType = 'theological' | 'scripture' | 'quote' | 'expand' | 'historical' | 'analogy' | 'simplify' | 'polish' | 'application_points';

interface EnhancementOption {
  id: EnhancementType;
  title: string;
  description: string;
  icon: React.ReactNode;
  isSuggestion?: boolean;
}

// --- Word-level diff ---

type DiffToken = { word: string; added: boolean };

function normalize(w: string): string {
  return w.toLowerCase().replace(/[.,!?;:'"()[\]{}—–\-]/g, '');
}

function computeWordDiff(original: string, enhanced: string): DiffToken[] | null {
  const origWords = original.trim().split(/\s+/).filter(Boolean);
  const enhWords = enhanced.trim().split(/\s+/).filter(Boolean);

  // Skip diff for very long texts to keep rendering fast
  if (origWords.length > 500 || enhWords.length > 500) return null;

  const m = origWords.length;
  const n = enhWords.length;

  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = normalize(origWords[i - 1]) === normalize(enhWords[j - 1])
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  const result: DiffToken[] = [];
  let i = m, j = n;
  while (j > 0) {
    if (
      i > 0 &&
      dp[i][j] === dp[i - 1][j - 1] + 1 &&
      normalize(origWords[i - 1]) === normalize(enhWords[j - 1])
    ) {
      result.unshift({ word: enhWords[j - 1], added: false });
      i--; j--;
    } else if (i === 0 || dp[i][j - 1] >= dp[i - 1][j]) {
      result.unshift({ word: enhWords[j - 1], added: true });
      j--;
    } else {
      i--;
    }
  }

  return result;
}

function getDiffSummary(tokens: DiffToken[], origText: string): string {
  const addedCount = tokens.filter(t => t.added).length;
  const origWordCount = origText.trim().split(/\s+/).filter(Boolean).length;
  const enhWordCount = tokens.length;
  const netChange = enhWordCount - origWordCount;

  if (addedCount === 0) return 'No changes detected';
  if (addedCount > enhWordCount * 0.65) return 'Substantially rewritten';
  if (netChange < -4) return `Condensed · ~${addedCount} word${addedCount !== 1 ? 's' : ''} changed`;
  return `~${addedCount} word${addedCount !== 1 ? 's' : ''} added or changed`;
}

// --- Component ---

export function TextEnhancer({ selectedText, onClose, onApply }: TextEnhancerProps) {
  const [selectedOption, setSelectedOption] = useState<EnhancementType | null>(null);
  const [loading, setLoading] = useState(false);
  const [enhancedText, setEnhancedText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enhancementOptions: EnhancementOption[] = [
    {
      id: 'theological',
      title: 'Theological Depth',
      description: 'Deepen doctrine and spiritual insight',
      icon: <Sparkles className="h-5 w-5 text-electric" />,
    },
    {
      id: 'scripture',
      title: 'Scripture References',
      description: 'Weave in supporting Bible verses',
      icon: <BookOpen className="h-5 w-5 text-crimson" />,
    },
    {
      id: 'quote',
      title: 'Theologian Quote',
      description: 'Add a quote from a trusted voice',
      icon: <Quote className="h-5 w-5 text-gold" />,
    },
    {
      id: 'historical',
      title: 'Historical Context',
      description: 'Illustrate with history or culture',
      icon: <History className="h-5 w-5 text-midnight dark:text-soft-white" />,
    },
    {
      id: 'analogy',
      title: 'Analogy / Example',
      description: 'Make the concept relatable',
      icon: <Lightbulb className="h-5 w-5 text-gold" />,
    },
    {
      id: 'expand',
      title: 'Expand Text',
      description: 'Elaborate and develop further',
      icon: <ArrowRight className="h-5 w-5 text-emerald" />,
    },
    {
      id: 'simplify',
      title: 'Simplify Language',
      description: 'Plain, accessible language for all',
      icon: <AlignLeft className="h-5 w-5 text-electric" />,
    },
    {
      id: 'polish',
      title: 'Polish',
      description: 'Copy-edit grammar, flow, and impact',
      icon: <Wand2 className="h-5 w-5 text-gold" />,
    },
    {
      id: 'application_points',
      title: 'Application Points',
      description: 'Suggest actionable next steps',
      icon: <ListChecks className="h-5 w-5 text-emerald" />,
      isSuggestion: true,
    },
  ];

  const activeOption = enhancementOptions.find(o => o.id === selectedOption);

  const diffTokens = useMemo(() => {
    if (!enhancedText || activeOption?.isSuggestion) return null;
    return computeWordDiff(selectedText, enhancedText);
  }, [enhancedText, selectedText, activeOption]);

  const diffSummary = useMemo(() => {
    if (!diffTokens) return null;
    return getDiffSummary(diffTokens, selectedText);
  }, [diffTokens, selectedText]);

  const handleSelectOption = async (option: EnhancementType) => {
    setSelectedOption(option);
    setLoading(true);
    setError(null);
    setEnhancedText(null);

    try {
      const result = await enhanceText(selectedText, option);
      if (result.success) {
        setEnhancedText(result.content as string);
      } else {
        throw new Error(result.error || 'Failed to enhance text');
      }
    } catch (err) {
      console.error('Error enhancing text:', err);
      setError(err instanceof Error ? err.message : 'Failed to enhance text');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (enhancedText) onApply(enhancedText);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-charcoal rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-sand/20 dark:border-charcoal/60">
          <h2 className="text-xl font-semibold text-midnight dark:text-soft-white">Enhance Text</h2>
          <button onClick={onClose} className="p-1 text-slate hover:text-midnight dark:hover:text-soft-white rounded-full" type="button">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {!selectedOption ? (
            <>
              <div className="mb-5">
                <h3 className="text-sm font-medium text-slate dark:text-soft-white mb-2">Selected Text</h3>
                <div className="p-3 bg-sand/10 dark:bg-midnight/30 rounded-lg text-slate dark:text-soft-white text-sm max-h-24 overflow-y-auto">
                  {selectedText}
                </div>
              </div>

              <h3 className="text-sm font-medium text-midnight dark:text-soft-white mb-3">How would you like to enhance this text?</h3>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {enhancementOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleSelectOption(option.id)}
                    className="flex flex-col items-center gap-2 p-3 border border-sand/20 dark:border-charcoal/60 rounded-lg hover:border-electric/40 hover:bg-electric/5 dark:hover:bg-electric/10 text-center transition-colors group"
                    type="button"
                  >
                    <div className="flex items-center justify-center h-9 w-9 rounded-full bg-sand/10 dark:bg-midnight/30 group-hover:bg-electric/10 transition-colors">
                      {option.icon}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-midnight dark:text-soft-white leading-tight">{option.title}</p>
                      <p className="text-[10px] text-slate dark:text-soft-white/60 mt-0.5 leading-tight">{option.description}</p>
                    </div>
                    {option.isSuggestion && (
                      <span className="text-[9px] uppercase tracking-wide font-semibold text-emerald bg-emerald/10 px-1.5 py-0.5 rounded-full">Suggestions</span>
                    )}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="mb-5">
                <div className="flex items-center mb-4 gap-3">
                  <button
                    onClick={() => { setSelectedOption(null); setEnhancedText(null); setError(null); }}
                    className="text-electric hover:text-electric/80 text-sm"
                    type="button"
                  >
                    ← Back
                  </button>
                  <div className="flex items-center gap-2">
                    {activeOption?.icon}
                    <h3 className="text-base font-semibold text-midnight dark:text-soft-white">
                      {activeOption?.title}
                    </h3>
                    {activeOption?.isSuggestion && (
                      <span className="text-[9px] uppercase tracking-wide font-semibold text-emerald bg-emerald/10 px-1.5 py-0.5 rounded-full">Suggestions only</span>
                    )}
                  </div>
                </div>

                {/* Diff summary bar — shown above both panels once result is ready */}
                {diffSummary && !loading && !error && (
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-electric shrink-0" />
                    <span className="text-xs text-electric font-medium">{diffSummary}</span>
                    <span className="text-xs text-slate dark:text-soft-white/50">· highlighted in blue below</span>
                  </div>
                )}

                <div className={`grid gap-5 ${activeOption?.isSuggestion ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                  {!activeOption?.isSuggestion && (
                    <div>
                      <h4 className="text-xs font-medium text-slate dark:text-soft-white/70 mb-2">Original</h4>
                      <div className="p-3 bg-sand/10 dark:bg-midnight/30 rounded-lg text-slate dark:text-soft-white text-sm h-48 overflow-y-auto leading-relaxed">
                        {selectedText}
                      </div>
                    </div>
                  )}

                  <div>
                    <h4 className="text-xs font-medium text-slate dark:text-soft-white/70 mb-2">
                      {activeOption?.isSuggestion ? 'Suggested Application Points' : 'Enhanced'}
                    </h4>
                    {loading ? (
                      <div className="p-3 bg-sand/10 dark:bg-midnight/30 rounded-lg h-48 flex items-center justify-center">
                        <div className="text-center">
                          <Loader2 className="h-8 w-8 animate-spin text-electric mx-auto mb-2" />
                          <p className="text-slate dark:text-soft-white/70 text-sm">
                            {activeOption?.isSuggestion ? 'Generating suggestions...' : 'Enhancing your text...'}
                          </p>
                        </div>
                      </div>
                    ) : error ? (
                      <div className="p-3 bg-crimson/10 rounded-lg h-48 flex items-center justify-center">
                        <div className="text-center text-crimson">
                          <p className="mb-2 text-sm">{error}</p>
                          <button
                            onClick={() => handleSelectOption(selectedOption)}
                            className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90 text-sm"
                            type="button"
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    ) : enhancedText ? (
                      activeOption?.isSuggestion ? (
                        <div className="p-3 bg-emerald/5 border border-emerald/20 rounded-lg text-slate dark:text-soft-white text-sm overflow-y-auto whitespace-pre-wrap min-h-48 max-h-72 leading-relaxed">
                          {enhancedText}
                        </div>
                      ) : diffTokens ? (
                        <div className="p-3 bg-emerald/10 dark:bg-emerald/5 rounded-lg h-48 overflow-y-auto text-sm leading-relaxed">
                          <p className="text-slate dark:text-soft-white">
                            {diffTokens.map((token, idx) => (
                              <React.Fragment key={idx}>
                                {idx > 0 ? ' ' : ''}
                                {token.added ? (
                                  <mark className="bg-electric/25 dark:bg-electric/30 text-midnight dark:text-soft-white rounded px-0.5 not-italic">
                                    {token.word}
                                  </mark>
                                ) : (
                                  token.word
                                )}
                              </React.Fragment>
                            ))}
                          </p>
                        </div>
                      ) : (
                        // Fallback for very long texts where diff was skipped
                        <div className="p-3 bg-emerald/10 dark:bg-emerald/5 rounded-lg h-48 overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap text-slate dark:text-soft-white">
                          {enhancedText}
                        </div>
                      )
                    ) : null}
                  </div>
                </div>
              </div>

              {enhancedText && !loading && !error && (
                <div className="flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm text-slate hover:text-midnight dark:hover:text-soft-white"
                    type="button"
                  >
                    {activeOption?.isSuggestion ? 'Done' : 'Cancel'}
                  </button>
                  {!activeOption?.isSuggestion && (
                    <button
                      onClick={handleApply}
                      className="px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 flex items-center gap-2 text-sm"
                      type="button"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Apply Enhancement
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
